import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import SecondaryNav from './SecondaryNav';
import UserDropdown from './UserDropdown';

function Layout({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      if (api.isAuthenticated()) {
        setIsAuthenticated(true);
        try {
          const profile = await api.getProfile();
          setUser(profile);
        } catch (err) {
          // Token might be invalid, still show auth UI
          console.error('Failed to fetch profile:', err);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    };

    checkAuth();
  }, [location.pathname]);

  return (
    <div className="font-sans text-slate-900 bg-background min-h-screen">
      {/* Primary Navigation */}
      <nav className="bg-primary py-4 px-6 sticky top-0 z-50 shadow-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <div className="bg-white rounded-lg p-1.5 shadow-inner">
              <i className="fas fa-shield-check text-secondary text-2xl"></i>
            </div>
            <span className="text-white font-bold text-xl tracking-tight uppercase">
              SCOPE <span className="text-secondary">ORDER</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center space-x-10">
            <div className="flex items-center space-x-8 text-white/90 font-medium">
              <a href="/#features" className="hover:text-secondary transition-colors duration-200">Features</a>
              <Link to="/pricing" className="hover:text-secondary transition-colors duration-200">Pricing</Link>
              <Link to="/help" className="hover:text-secondary transition-colors duration-200">Help</Link>
            </div>

            {isAuthenticated ? (
              <UserDropdown user={user} />
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login" className="text-white/90 hover:text-white font-medium transition-colors duration-200">
                  Sign In
                </Link>
                <Link to="/upgrade"
                   className="bg-secondary text-white px-6 py-2.5 rounded-xl font-bold hover:bg-orange-600 transition-all duration-200 shadow-lg active:scale-95">
                  Get Pro
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            {isAuthenticated ? (
              <UserDropdown user={user} />
            ) : (
              <Link to="/login" className="text-white/90 hover:text-white font-medium">
                Sign In
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Secondary Navigation (authenticated users only) */}
      {isAuthenticated && <SecondaryNav />}

      {/* Page Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="py-16 px-6 bg-slate-900 text-slate-500">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12">
            <div className="flex items-start space-x-2">
              <i className="fas fa-shield-check text-secondary opacity-50 text-2xl"></i>
              <div>
                <span className="font-black text-white tracking-widest uppercase block mb-2">SCOPE ORDER</span>
                <p className="text-sm text-slate-400 max-w-xs">Professional change order management for contractors.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              <div>
                <h3 className="font-bold text-white mb-4 uppercase tracking-widest text-sm">Product</h3>
                <ul className="space-y-2 text-sm">
                  <li><a href="/#features" className="hover:text-white transition">Features</a></li>
                  <li><Link to="/pricing" className="hover:text-white transition">Pricing</Link></li>
                  <li><Link to="/upgrade" className="hover:text-white transition">Get Pro</Link></li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-white mb-4 uppercase tracking-widest text-sm">Support</h3>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/help" className="hover:text-white transition">Help Center</Link></li>
                  <li><Link to="/account" className="hover:text-white transition">My Account</Link></li>
                  <li><a href="mailto:support@scopeorder.com" className="hover:text-white transition">Contact Us</a></li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-white mb-4 uppercase tracking-widest text-sm">Legal</h3>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/terms" className="hover:text-white transition">Terms of Service</Link></li>
                  <li><Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-xs font-medium opacity-40">
              © 2026 SCOPE ORDER. VERSION 1.0.0
            </div>
            <div className="flex items-center space-x-4">
              <i className="fab fa-apple text-2xl opacity-30"></i>
              <i className="fab fa-google-play text-2xl opacity-30"></i>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
